import { useState, useRef, useCallback } from 'react';

/**
 * useSlideHighlight
 * Shared logic for the sliding box highlight animation.
 *
 * @param {'x' | 'y'} axis - 'x' for horizontal (nav), 'y' for vertical (experience rows)
 * @param {{ useHome?: boolean }} options
 *   useHome: if true, the highlight slides off past the first item on mouse leave
 * @returns {{ containerRef, boxStyle, handleMouseEnter, handleMouseLeave, snapToElement, registerHome }}
 */
export const useSlideHighlight = (axis = 'x', options = {}) => {
  const { useHome = false } = options;

  const [boxStyle, setBoxStyle] = useState({});
  const homeElementRef = useRef(null);
  const containerRef = useRef(null);

  const registerHome = useCallback((element) => {
    homeElementRef.current = element;
  }, []);

  const styleForElement = useCallback((element) => {
    if (!element || !containerRef.current) return null;
    const containerRect = containerRef.current.getBoundingClientRect();
    const itemRect = element.getBoundingClientRect();
    return axis === 'x'
      ? {
          width: `${itemRect.width}px`,
          height: `${itemRect.height}px`,
          transform: `translateX(${itemRect.left - containerRect.left}px)`,
        }
      : {
          height: `${itemRect.height}px`,
          transform: `translateY(${itemRect.top - containerRect.top}px)`,
        };
  }, [axis]);

  const getExitStyle = useCallback(() => {
    if (!homeElementRef.current || !containerRef.current) return null;
    const containerRect = containerRef.current.getBoundingClientRect();
    const itemRect = homeElementRef.current.getBoundingClientRect();
    const height = itemRect.height;
    return axis === 'x'
      ? {
          width: `${itemRect.width}px`,
          height: `${height}px`,
          transform: `translateX(${itemRect.left - containerRect.left - itemRect.width}px)`,
        }
      : {
          height: `${height}px`,
          transform: `translateY(${itemRect.top - containerRect.top - height}px)`,
        };
  }, [axis]);

  const handleMouseEnter = useCallback((e) => {
    const style = styleForElement(e.currentTarget);
    if (style) setBoxStyle({ ...style, opacity: 1 });
  }, [styleForElement]);

  const handleMouseLeave = useCallback(() => {
    const exit = useHome ? getExitStyle() : null;
    if (exit) {
      setBoxStyle(prev => ({ ...prev, ...exit, opacity: 0 }));
    } else {
      setBoxStyle(prev => ({ ...prev, opacity: 0 }));
    }
  }, [useHome, getExitStyle]);

  const snapToElement = useCallback((element) => {
    const style = styleForElement(element);
    if (style) setBoxStyle({ ...style, opacity: 1 });
  }, [styleForElement]);

  return { containerRef, boxStyle, handleMouseEnter, handleMouseLeave, snapToElement, registerHome };
};
